import React, { useEffect, useMemo, useState } from 'react';
import DENTModalOverviewSubButtonsSelection from '../../DENTModalOverviewSubButtonsSelection/DENTModalOverviewSubButtonsSelection';
import {
	ToothDecayGrade,
	ToothDecayPulp,
	ToothDecayStage,
	ToothDecayType,
	ToothPathologyDecayValues,
} from '../../../../../graphql/graphql';
import useTooth from '../../../Hooks/Tooth/useTooth';
import { useDENTContext } from '../../../Services/DENTContext';
import { PATHOLOGY_TRANSLATION_MAP } from '../../../Helpers/pathology/pathologyTranslationMap.helper';
import styles from './DENTModalOverviewPathologyDecay.module.scss';
import DENTMultipleSelectList from '../../DENTMultipleSelectList/DENTMultipleSelectList';
import DENTModalOverviewPathologyDecayListItem from './DENTModalOverviewPathologyDecayListItem';

interface Props {
	decayIndex: number;
	setDecayIndex: (index: number) => void;
}

const DENTModalOverviewPathologyDecay = ({
	decayIndex,
	setDecayIndex,
}: Props) => {
	const {
		pathology: { decays },
	} = useTooth();
	const { activeTooth } = useDENTContext();

	const [values, setValues] = useState<ToothPathologyDecayValues>(
		decays.getValues(decayIndex),
	);

	useEffect(() => {
		if (values) {
			decays.setValues(decayIndex, values);
		}
	}, [values]);

	useEffect(() => {
		const newValues = decays.getValues(decayIndex) ?? {};
		decays.setValues(decayIndex, newValues);
		setValues(newValues);
	}, [decayIndex, setDecayIndex]);

	const handleChangeType = (value: ToothDecayType) =>
		setValues(prev =>
			prev?.type === value
				? {
						type: undefined,
						stage: undefined,
						pulp: undefined,
						grade: undefined,
				  }
				: { ...prev, type: value },
		);

	const handleChangeStage = (value: ToothDecayStage) =>
		setValues(prev =>
			prev?.stage === value
				? {
						...prev,
						stage: undefined,
						pulp: undefined,
						grade: undefined,
				  }
				: {
						...prev,
						stage: value,
						pulp: undefined,
						grade: undefined,
				  },
		);

	const handleChangePulp = (value: ToothDecayPulp) =>
		setValues(prev =>
			prev?.pulp === value
				? {
						...prev,
						pulp: undefined,
						grade: undefined,
				  }
				: { ...prev, pulp: value },
		);

	const handleChangeGrade = (value: ToothDecayGrade) =>
		setValues(prev => ({
			...prev,
			grade: prev?.grade === value ? undefined : value,
		}));

	const getDecayTypeRowItem = (item: ToothDecayType) => ({
		title: PATHOLOGY_TRANSLATION_MAP[item],
		isSelected: values?.type === item,
		onClick: () => handleChangeType(item),
	});

	const canAddNewDecay = useMemo(() => {
		const activeDecay = activeTooth?.pathology?.decays?.[decayIndex];
		return Boolean(activeDecay?.surfaces?.length && activeDecay?.values?.type);
	}, [activeTooth?.pathology?.decays?.[decayIndex]]);

	const rows = [
		{
			shouldHide: false,
			items: [
				getDecayTypeRowItem(ToothDecayType.Primary),
				getDecayTypeRowItem(ToothDecayType.Secondary),
			],
		},
		{
			shouldHide: !values?.type,
			items: [
				{
					title:
						'patientTreatment.meddiDENT.dentalCross.pathologyModal.decay.stageDentin',
					isSelected: values?.stage === ToothDecayStage.Dentin,
					onClick: () => handleChangeStage(ToothDecayStage.Dentin),
				},
				{
					title:
						'patientTreatment.meddiDENT.dentalCross.pathologyModal.decay.stageEnamel',
					isSelected: values?.stage === ToothDecayStage.Enamel,
					onClick: () => handleChangeStage(ToothDecayStage.Enamel),
				},
			],
		},
		{
			shouldHide: !(values?.type && values?.stage === ToothDecayStage.Dentin),
			items: [
				{
					title:
						'patientTreatment.meddiDENT.dentalCross.pathologyModal.decay.pulpInvolved',
					isSelected: values?.pulp === ToothDecayPulp.Involved,
					onClick: () => handleChangePulp(ToothDecayPulp.Involved),
				},
				{
					title:
						'patientTreatment.meddiDENT.dentalCross.pathologyModal.decay.pulpNotInvolved',
					isSelected: values?.pulp === ToothDecayPulp.NotInvolved,
					onClick: () => handleChangePulp(ToothDecayPulp.NotInvolved),
				},
			],
		},
		{
			shouldHide: !(
				values?.type &&
				values?.stage &&
				(values?.stage === ToothDecayStage.Enamel ||
					(values?.stage === ToothDecayStage.Dentin && values?.pulp))
			),
			items: [
				{
					title:
						'patientTreatment.meddiDENT.dentalCross.pathologyModal.decay.gradeA',
					isSelected: values?.grade === ToothDecayGrade.GradeA,
					onClick: () => handleChangeGrade(ToothDecayGrade.GradeA),
				},
				{
					title:
						'patientTreatment.meddiDENT.dentalCross.pathologyModal.decay.gradeB',
					isSelected: values?.grade === ToothDecayGrade.GradeB,
					onClick: () => handleChangeGrade(ToothDecayGrade.GradeB),
				},
				{
					title:
						'patientTreatment.meddiDENT.dentalCross.pathologyModal.decay.gradeC',
					isSelected: values?.grade === ToothDecayGrade.GradeC,
					onClick: () => handleChangeGrade(ToothDecayGrade.GradeC),
				},
			],
		},
	];

	return (
		<div className={styles.wrapper}>
			<DENTModalOverviewSubButtonsSelection rows={rows} />
			<DENTMultipleSelectList
				items={activeTooth?.pathology.decays || []}
				addNewItem={() => setDecayIndex(decayIndex + 1)}
				showAddButton={canAddNewDecay}
				renderComponent={(item, index) => (
					<DENTModalOverviewPathologyDecayListItem
						key={index}
						decay={item}
						handleEdit={() => setDecayIndex(index)}
						handleDelete={() => decays.reset(index)}
					/>
				)}
			/>
		</div>
	);
};

export default DENTModalOverviewPathologyDecay;
